/**
 * FreshnessSummaryWidget — Hygiène des analyses (Doctrine 47 — V23.14.7b).
 *
 * Widget agrégé pour Executive 360° qui répond à la question :
 * "Combien de mes analyses sont à jour ? Lesquelles devraient être rafraîchies ?"
 *
 * Source : GET /api/workspace/cockpit/freshness-summary
 *
 * Affichage :
 *   - Gros chiffre central : % de modules frais
 *   - Mini-jauge tricolore (vert/orange/rouge)
 *   - Top 3 modules à rafraîchir (les plus vieux) + bouton "Ouvrir"
 *
 * Click sur un module → navigation vers son setup_route (l'utilisateur peut
 * relancer manuellement la génération).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Sparkles, AlertCircle, AlertTriangle, CheckCircle2, ExternalLink, Loader2, ChevronDown, ChevronUp, CircleDashed } from "lucide-react";

const STATE_META = {
  fresh:  { color: "#1D9E75", Icon: CheckCircle2, label: "Frais" },
  aging:  { color: "#EF9F27", Icon: AlertTriangle, label: "Vieillissant" },
  stale:  { color: "#E24B4A", Icon: AlertCircle, label: "Obsolète" },
  absent: { color: "#52525B", Icon: CircleDashed, label: "Jamais généré" },
};

export default function FreshnessSummaryWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/workspace/cockpit/freshness-summary");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="noam-card p-4 flex items-center gap-2 text-zinc-500 text-sm" data-testid="freshness-widget-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("freshness_widget.loading", { defaultValue: "Chargement…" })}
      </div>
    );
  }
  if (!data) return null;

  const { fresh_count, aging_count, stale_count, absent_count, instrumented_count, freshness_pct, modules } = data;
  const nonAbsent = instrumented_count - absent_count;
  const filledCount = fresh_count + aging_count + stale_count;
  // Top 3 urgents (stale > aging) + non-absent
  const urgent = (modules || []).filter((m) => m.state === "stale" || m.state === "aging").slice(0, 3);
  const pctColor = freshness_pct >= 70 ? "#1D9E75" : freshness_pct >= 40 ? "#EF9F27" : "#E24B4A";

  // V23.14.11 — Groupes pour l'expansion
  const groups = [
    { state: "stale",  title: "À rafraîchir maintenant",     items: (modules || []).filter((m) => m.state === "stale") },
    { state: "aging",  title: "Bientôt obsolète",            items: (modules || []).filter((m) => m.state === "aging") },
    { state: "absent", title: "Jamais généré — à remplir",   items: (modules || []).filter((m) => m.state === "absent") },
    { state: "fresh",  title: "À jour",                       items: (modules || []).filter((m) => m.state === "fresh") },
  ];

  return (
    <div className="noam-card p-4" data-testid="freshness-widget">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="noam-overline">{t("freshness_widget.overline", { defaultValue: "Doctrine 47 · Hygiène des analyses" })}</div>
          <div className="noam-display text-base font-semibold flex items-center gap-2">
            {t("freshness_widget.title", { defaultValue: "Vos analyses sont-elles fraîches ?" })}
          </div>
          {/* V23.14.11 — Compteur explicite « X modules remplis sur Y » */}
          <div className="text-[11px] text-zinc-400 mt-1.5" data-testid="freshness-widget-filled-ratio">
            <span className="font-semibold text-zinc-200">{filledCount}</span>
            <span className="text-zinc-500"> / {instrumented_count} </span>
            {t("freshness_widget.filled_ratio", { defaultValue: "modules remplis" })}
            {absent_count > 0 && (
              <span className="text-[#EF9F27] ml-1">· {absent_count} à remplir</span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-3xl font-bold leading-none" style={{ color: pctColor }} data-testid="freshness-widget-pct">
            {freshness_pct}%
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">
            {t("freshness_widget.fresh_ratio", { defaultValue: "frais" })}
          </div>
        </div>
      </div>

      {/* Mini-jauge tricolore */}
      {nonAbsent > 0 && (
        <div className="flex w-full h-1.5 rounded-full overflow-hidden bg-white/5 mb-3" data-testid="freshness-widget-gauge">
          {fresh_count > 0 && <div style={{ width: `${(100 * fresh_count) / nonAbsent}%`, background: "#1D9E75" }} title={`Frais : ${fresh_count}`} />}
          {aging_count > 0 && <div style={{ width: `${(100 * aging_count) / nonAbsent}%`, background: "#EF9F27" }} title={`Vieillissant : ${aging_count}`} />}
          {stale_count > 0 && <div style={{ width: `${(100 * stale_count) / nonAbsent}%`, background: "#E24B4A" }} title={`Obsolète : ${stale_count}`} />}
        </div>
      )}

      {/* Compteurs */}
      <div className="flex items-center gap-3 text-[11px] text-zinc-400 mb-3">
        <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-[#1D9E75]" />{fresh_count} frais</span>
        <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-[#EF9F27]" />{aging_count} vieillissant</span>
        <span className="inline-flex items-center gap-1"><AlertCircle className="w-3 h-3 text-[#E24B4A]" />{stale_count} obsolète</span>
        {absent_count > 0 && (
          <span className="inline-flex items-center gap-1 text-zinc-600">· {absent_count} jamais généré</span>
        )}
      </div>

      {/* Top 3 modules urgents */}
      {urgent.length > 0 ? (
        <div className="border-t border-white/5 pt-3 space-y-1.5" data-testid="freshness-widget-urgent">
          <div className="noam-overline mb-1">
            {t("freshness_widget.to_refresh", { defaultValue: "À rafraîchir en priorité" })}
          </div>
          {urgent.map((m) => {
            const meta = STATE_META[m.state] || STATE_META.absent;
            const Icon = meta.Icon;
            return (
              <Link
                key={m.key}
                to={m.setup_route}
                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-white/[0.04] group"
                data-testid={`freshness-widget-row-${m.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: meta.color }} />
                  <span className="text-xs text-zinc-200 truncate">{m.label}</span>
                  <span className="text-[10px] text-zinc-500 flex-shrink-0">· {m.days}j</span>
                </div>
                <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-zinc-300 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      ) : nonAbsent > 0 ? (
        <div className="border-t border-white/5 pt-3 text-[11px] text-[#1D9E75] flex items-center gap-1.5" data-testid="freshness-widget-allclean">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {t("freshness_widget.all_clean", { defaultValue: "Toutes vos analyses sont fraîches. Beau travail." })}
        </div>
      ) : (
        <div className="border-t border-white/5 pt-3 text-[11px] text-zinc-500 italic" data-testid="freshness-widget-empty">
          {t("freshness_widget.empty", { defaultValue: "Aucune analyse générée pour le moment. Lancez vos premières analyses dans les modules ci-dessus." })}
        </div>
      )}

      {/* V23.14.11 — Toggle « Voir tous les modules » : permet aux débutants et
          intermédiaires de visualiser EXACTEMENT quel module est rempli /
          vieillissant / vide, et de cliquer pour aller le remplir.
      */}
      <div className="border-t border-white/5 mt-3 pt-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-[11px] text-zinc-400 hover:text-white transition-colors py-1"
          data-testid="freshness-widget-toggle-all"
        >
          <span>
            {expanded
              ? t("freshness_widget.hide_all", { defaultValue: "Masquer le détail" })
              : t("freshness_widget.show_all", { defaultValue: "Voir tous les modules" })}
            <span className="text-zinc-600 ml-1">· {instrumented_count}</span>
          </span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>

        {expanded && (
          <div className="mt-2 space-y-3" data-testid="freshness-widget-all-modules">
            {groups.map((g) => {
              if (g.items.length === 0) return null;
              const meta = STATE_META[g.state];
              const Icon = meta.Icon;
              return (
                <div key={g.state}>
                  <div className="flex items-center gap-1.5 mb-1.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: meta.color }}>
                    <Icon className="w-3 h-3" />
                    <span>{g.title}</span>
                    <span className="text-zinc-600 normal-case tracking-normal">· {g.items.length}</span>
                  </div>
                  <div className="space-y-1">
                    {g.items.map((m) => (
                      <Link
                        key={m.key}
                        to={m.setup_route}
                        className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-white/[0.04] group border border-transparent hover:border-white/10"
                        data-testid={`freshness-widget-all-row-${m.key}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className="w-3 h-3 flex-shrink-0" style={{ color: meta.color }} />
                          <span className="text-[11px] text-zinc-200 truncate">{m.label}</span>
                          {m.state !== "absent" && m.days != null && (
                            <span className="text-[10px] text-zinc-600 flex-shrink-0">
                              · {m.days === 0 ? "aujourd'hui" : `${m.days}j`}
                            </span>
                          )}
                          {m.count > 0 && (
                            <span className="text-[9px] text-zinc-600 flex-shrink-0 ml-0.5">
                              ({m.count})
                            </span>
                          )}
                        </div>
                        <span className="flex items-center gap-1 flex-shrink-0">
                          {m.state === "absent" && (
                            <span className="text-[9px] text-[#EF9F27] uppercase tracking-wider hidden sm:inline">
                              {t("freshness_widget.fill_now", { defaultValue: "Remplir" })}
                            </span>
                          )}
                          {m.state !== "absent" && (m.state === "stale" || m.state === "aging") && (
                            <span className="text-[9px] uppercase tracking-wider hidden sm:inline" style={{ color: meta.color }}>
                              {t("freshness_widget.refresh", { defaultValue: "Rafraîchir" })}
                            </span>
                          )}
                          <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-zinc-300" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
